
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Menu } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { usePermissions } from "@/hooks/use-permissions";
import { useIsMobile } from "@/hooks/use-mobile";
import AdminSidebar from "@/components/admin/AdminSidebar";
import AdminHeader from "@/components/admin/AdminHeader";
import CountryManagement from "@/components/admin/CountryManagement";
import CountryConstituencies from "@/components/admin/CountryConstituencies";
import RegionsManagement from "@/components/admin/regions/RegionsManagement";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

const AdminCountries = () => {
  const navigate = useNavigate();
  const { user, profile, isLoading } = useAuth();
  const { isSuperAdmin, isAdmin } = usePermissions();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  
  useEffect(() => {
    // Set document title
    document.title = "Country Management - NationCard";
    
    console.log("AdminCountries permissions check:", { 
      userEmail: user?.email,
      userRole: profile?.role,
      isAdmin: isAdmin(),
      isSuperAdmin: isSuperAdmin()
    });
    
    // Only perform this check when auth loading is complete
    if (!isLoading) {
      if (!user) {
        console.log("No user found, redirecting to auth");
        toast.error("Please log in to access the admin dashboard");
        navigate("/auth");
        return;
      }
      
      if (!isAdmin() && !isSuperAdmin()) {
        console.log("User lacks admin permissions, redirecting to home");
        toast.error("You do not have permission to access the admin dashboard");
        navigate("/");
        return;
      }
    }
  }, [user, profile, isAdmin, isSuperAdmin, navigate, isLoading]);

  // Effect to handle sidebar state on mobile/desktop switch
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);
  
  if (isLoading) {
    return <div className="flex h-screen w-full items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-blue mx-auto mb-4"></div>
        <p className="text-nation-blue">Loading admin dashboard...</p>
      </div>
    </div>;
  }
  
  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar toggle */}
      {isMobile && !sidebarOpen && (
        <Button
          variant="outline"
          size="icon"
          className="fixed top-4 left-4 z-40 md:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-4 w-4" />
        </Button>
      )}
      
      {/* Sidebar with conditional display for mobile */}
      <div className={`${sidebarOpen ? "block" : "hidden"} ${isMobile ? "fixed inset-0 z-50 bg-black/50" : ""}`} onClick={isMobile ? () => setSidebarOpen(false) : undefined}>
        <div onClick={e => isMobile && e.stopPropagation()} className={isMobile ? "h-full w-64" : ""}>
          <AdminSidebar onClose={isMobile ? () => setSidebarOpen(false) : undefined} />
        </div>
      </div>
      
      <div className="flex-1">
        <AdminHeader>
          {isMobile && (
            <Button
              variant="outline"
              size="icon"
              className="mr-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
          Geo Management
        </AdminHeader>
        
        <main className="p-4 md:p-6 overflow-x-auto">
          <div className="mb-4">
            <div className="text-sm text-gray-500 mb-1">
              Admin / Geo Management
            </div>
          </div>
          
          <h1 className="text-2xl font-bold mb-6">Geographic Management</h1>
          
          <Tabs defaultValue="countries" className="mb-6">
            <TabsList className={`${isMobile ? 'w-full' : 'w-auto'}`}>
              <TabsTrigger value="regions" className={isMobile ? "flex-1" : ""}>Regions</TabsTrigger>
              <TabsTrigger value="countries" className={isMobile ? "flex-1" : ""}>Countries</TabsTrigger>
              <TabsTrigger value="constituencies" className={isMobile ? "flex-1" : ""}>Constituencies</TabsTrigger>
            </TabsList>
            
            <TabsContent value="regions" className="mt-4">
              <RegionsManagement />
            </TabsContent>
            
            <TabsContent value="countries" className="mt-4">
              <CountryManagement isMobile={isMobile} />
            </TabsContent>
            
            <TabsContent value="constituencies" className="mt-4">
              <CountryConstituencies />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default AdminCountries;
